import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, Warning as WarningIcon, AccessTime as AccessTimeIcon, Check as CheckIcon } from '@mui/icons-material';
import { generatePasswordResetUrl as generatePatientPasswordResetUrl } from '../../api/patients';
import { generatePasswordResetUrl as generateNursePasswordResetUrl } from '../../api/nurses';

interface PasswordResetDialogProps {
  open: boolean;
  userId: number;
  userName: string;
  userType: 'patient' | 'nurse';
  onClose: () => void;
}

const PasswordResetDialog: React.FC<PasswordResetDialogProps> = ({
  open,
  userId,
  userName,
  userType,
  onClose,
}) => {
  const [ withNotification, setWithNotification ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const [ result, setResult ] = useState<{ reset_url: string; expires_at: string } | null>(null);
  const [ copied, setCopied ] = useState(false);
  const [ showCloseConfirm, setShowCloseConfirm ] = useState(false);
  
  // 有効期限までの残り時間を計算
  const expiryInfo = useMemo(() => {
    if (!result?.expires_at) return null;
    
    const expiryTime = new Date(result.expires_at).getTime();
    const now = new Date().getTime();
    
    if (now > expiryTime) {
      return { isExpired: true, remainingTime: '有効期限切れ' };
    }
    
    const remaining = expiryTime - now;
    const hours = Math.floor(remaining / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    
    if (hours > 0) {
      return { isExpired: false, remainingTime: `残り ${hours}時間${minutes}分` };
    } else {
      return { isExpired: false, remainingTime: `残り ${minutes}分` };
    }
  }, [result?.expires_at]);
  const handleSubmit = async() => {
    setIsLoading(true);
    setError(null);
    try {
      const generateUrl = userType === 'patient' ? generatePatientPasswordResetUrl : generateNursePasswordResetUrl;
      const response = await generateUrl(userId, withNotification);
      setResult(response);
    } catch (error: any) {
      console.error('パスワードリセットURL生成エラー:', error);
      const errorMessage = error.response?.data?.error || 'パスワードリセットURLの生成に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCopyUrl = () => {
    if (result?.reset_url) {
      navigator.clipboard.writeText(result.reset_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // 2秒後に元に戻す
    }
  };
  const handleClose = () => {
    // 状態をリセット
    setWithNotification(false);
    setError(null);
    setResult(null);
    setCopied(false);
    setShowCloseConfirm(false);
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // backdropClickやescapeKeyDownによる閉じるを無効化
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>パスワードリセットURL発行</DialogTitle>
      <DialogContent>
        {!result ? (
          // 初期画面
          <>
            <DialogContentText sx={{ mb: 3 }}>
              <strong>{userName}</strong>さんのパスワードリセットURLを生成します。
            </DialogContentText>

            <FormControlLabel
              control={
                <Checkbox
                  checked={withNotification}
                  onChange={(e) => setWithNotification(e.target.checked)}
                  disabled={isLoading}
                />
              }
              label="リセットメールを送信する"
              sx={{ mb: 2 }}
            />

            <Typography variant="body2" color="text.secondary">
              チェックを入れると、{userType === 'patient' ? '患者' : '看護師'}のメールアドレスにパスワードリセットメールが送信されます。
              チェックを外した場合は、URLのみ生成されます。
            </Typography>
          </>
        ) : result && showCloseConfirm ? (
          // 確認画面
          <>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                URLをコピーしましたか？
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                画面を閉じるとURLは再取得できません。
              </Typography>
            </Alert>
          </>
        ) : (
          // 結果表示画面
          <>
            <Alert severity="warning" icon={<WarningIcon/>} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                URLは画面を閉じると再取得できません。必ずコピーしてください。
              </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                パスワードリセットURL
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  value={result.reset_url}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                  size="small"
                  multiline
                  rows={2}
                />
                <Button
                  variant="outlined"
                  onClick={handleCopyUrl}
                  startIcon={copied ? <CheckIcon/> : <ContentCopyIcon/>}
                  sx={{ flexShrink: 0 }}
                  color={copied ? "success" : "primary"}
                >
                  {copied ? 'コピーしました' : 'コピー'}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
              <Typography
                component="span"
                sx={{
                  fontSize: '0.9rem',
                  color: expiryInfo?.isExpired ? 'error.main' : 'text.secondary'
                }}
              >
                有効期限: {expiryInfo?.remainingTime || '計算中...'}
              </Typography>
            </Box>

            {withNotification && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {userType === 'patient' ? '患者' : '看護師'}のメールアドレスにパスワードリセットメールを送信しました。
              </Alert>
            )}
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {!result ? (
          <>
            <Button onClick={handleClose} disabled={isLoading}>
              キャンセル
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20}/> : null}
            >
              {isLoading ? '処理中...' : 'URL発行'}
            </Button>
          </>
        ) : showCloseConfirm ? (
          // 確認画面のボタン
          <>
            <Button onClick={() => setShowCloseConfirm(false)} variant="outlined">
              戻る
            </Button>
            <Button onClick={handleClose} variant="contained">
              コピーせずに閉じる
            </Button>
          </>
        ) : (
          // 結果表示画面のボタン
          <Button 
            onClick={() => !copied ? setShowCloseConfirm(true) : handleClose()} 
            variant="contained"
          >
            閉じる
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
export default PasswordResetDialog;
